import React, { useEffect, useState } from "react";
import { Card, Col, Form, Row, DatePicker, Checkbox, Divider, Select, Button } from "antd";
import { useHistory, useRouteMatch } from "react-router";
import { v4 as uuid } from "uuid";
import moment from "moment";
import type { Moment } from "moment";
import { RootStateOrAny } from "react-redux";
import { FormInstance } from "antd/es/form/Form";
import { fetchDocumentTemplates } from "../../../store/slices/documentTemplates";
import loadingStatusCodes from "../../../lib/const/loadingStatusCodes";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  checkIsLoading,
  displayFormat,
  infinityDateHttpFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../lib/utils";

const styles = {
  selectAgreement: {
    width: "350px",
  },
  selectService: {
    width: "100%",
  },
  emails: {
    width: "360px",
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    color: "#8c8c8c",
  },
  radioStyle: {
    lineHeight: "30px",
  },
  checkbox: {
    marginTop: "5px",
  },
  selectTemplate: {
    width: "350px",
  },
  selectFormat: {
    width: "100%",
  },
};

const { RangePicker } = DatePicker;
const dateFormat = displayFormat;

interface IProps {
  cancelText: string;
  productFromDate: Moment;
  productToDate?: Moment;
  formCreateInvoicing?: FormInstance;
}

export const ProductsCreateInvoicing = ({
  cancelText = "Назад",
  productFromDate,
  productToDate,
  formCreateInvoicing,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { url } = useRouteMatch();

  const { billInfo } = useAppSelector((state: RootStateOrAny) => state.bills);

  const { account } = useAppSelector((state) => state.accounts);
  const { agreements } = useAppSelector((state) => state.agreements);
  const { addBillLoading } = useAppSelector((state) => state.bills);
  const history = useHistory();
  const [disabledFromDatesForCalendar, setDisabledFromDatesForCalendar] = useState("");
  const [disabledToDatesForCalendar, setDisabledToDatesForCalendar] = useState("");

  const { templates, templatesLoading } = useAppSelector((state) => state?.documentTemplates);

  const resetFields = () => {
    formCreateInvoicing && formCreateInvoicing.resetFields();
    setDisabledFromDatesForCalendar("");
    setDisabledToDatesForCalendar("");
  };

  const onSelectAgreement = (value) => {
    formCreateInvoicing &&
      formCreateInvoicing.setFieldsValue({
        agreementId: value,
      });

    const findAgreement =
      agreements &&
      agreements.length !== 0 &&
      (agreements as { id: number; validFrom: string; validTo: string }[]).filter(
        (filter) => filter.id === value
      )[0];

    setDisabledFromDatesForCalendar(
      findAgreement && findAgreement?.validFrom
        ? toHttpFormatStartOfDay(findAgreement?.validFrom)
        : ""
    );
    setDisabledToDatesForCalendar(
      findAgreement && findAgreement?.validTo ? toHttpFormatEndOfDay(findAgreement?.validTo) : ""
    );
  };

  const getDisabledDates = (cur: Moment) => {
    // if (disabledFromDatesForCalendar && disabledToDatesForCalendar) {
    //   const fromDateMoment = moment(disabledFromDatesForCalendar);
    //   const toDateMoment = moment(disabledToDatesForCalendar);
    return (
      moment(cur).isBefore(productFromDate) ||
      moment(cur).isAfter(productToDate || infinityDateHttpFormat)
    );
    // }
    // return false;
  };

  const renderSelectAgreement = () => (
    <Form.Item
      name="agreementId"
      rules={[{ required: true, message: "Пожалуйста, выберите договор" }]}
      style={{ width: "100%" }}
    >
      <Select
        placeholder="Выберите договор"
        style={styles.selectAgreement}
        onChange={onSelectAgreement}
        disabled
      >
        {agreements &&
          agreements.length !== 0 &&
          agreements.map((item: { id: number; agreementNumber: number }) => (
            <Select.Option key={uuid()} value={item.id}>
              {item.agreementNumber}
            </Select.Option>
          ))}
      </Select>
    </Form.Item>
  );

  const renderCheckbox = () => (
    <Form.Item name="sendBill" valuePropName="checked" noStyle>
      <Checkbox>
        <label>Отправить счет по адресам</label>
      </Checkbox>
    </Form.Item>
  );

  // Выбор шаблона по умолчанию
  useEffect(() => {
    if (templates && templates.length !== 0) {
      const findDefaultTemplate = templates.find((template) => template.defaultTemplate);
      formCreateInvoicing &&
        formCreateInvoicing.setFieldsValue({
          reportId: findDefaultTemplate?.id,
        });
    }
  }, [templates]);

  // Загрузка шаблонов
  useEffect(() => {
    const params: { onDate?: string; reportTypeId: number } = { reportTypeId: billInfo.billTypeId };
    params.onDate = billInfo.fromDate ? toHttpFormat(billInfo?.fromDate) : toHttpFormat(moment());
    dispatch(fetchDocumentTemplates(params));
  }, []);

  const renderSelectTemplates = () => (
    <Form.Item name="reportId">
      <Select
        placeholder="Выберите шаблон"
        style={styles.selectTemplate}
        loading={templatesLoading === loadingStatusCodes.pending}
        allowClear
      >
        {Array.isArray(templates) &&
          templates.length !== 0 &&
          templates.map((item) => (
            <Select.Option key={uuid()} value={item.id}>
              {item.defaultTemplate ? `${item.name} (по умолчанию)` : item.name}
            </Select.Option>
          ))}
      </Select>
    </Form.Item>
  );

  const renderSelectFormat = () => (
    <Form.Item name="formatType" initialValue="PDF">
      <Select placeholder="Выберите формат">
        {[{ value: "PDF" }, { value: "XLSX" }].map((item) => (
          <Select.Option key={uuid()} value={item.value}>
            {item.value}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  return (
    <Card title="" bordered={false}>
      <Row>
        <Col span={11} style={{ display: "flex" }}>
          {renderSelectAgreement()}
        </Col>
      </Row>

      <Row />

      <Form.Item
        label="Период счета"
        name="period"
        rules={[{ required: true, message: "Пожалуйста, введите период счёта" }]}
        wrapperCol={{ span: 6 }}
      >
        <RangePicker
          format={dateFormat}
          style={{ width: "300px" }}
          disabledDate={getDisabledDates}
        />
      </Form.Item>

      <Form.Item name="fromDate" label="Дата счета" wrapperCol={{ span: 6 }}>
        <DatePicker format={dateFormat} style={{ width: "150px", marginLeft: "22px" }} />
      </Form.Item>

      <Divider />

      <Row gutter={10} style={{ marginBottom: 100 }}>
        <Col>{renderSelectTemplates()}</Col>
        <Col>{renderSelectFormat()}</Col>
        <Col style={{ marginLeft: 100 }}>
          <Card
            headStyle={{ padding: "0 10px", margin: 0 }}
            title={renderCheckbox()}
            bodyStyle={styles.emails as any}
          >
            {account.billDeliveryEmails.map((e, index) => (
              <Row wrap={false} style={{ margin: "0 0 5px -19px" }} gutter={10}>
                <Col />
                <Col>
                  <span key={index} className="invoice__email">
                    {e.email}
                  </span>
                </Col>
              </Row>
            ))}
          </Card>
        </Col>
      </Row>

      <Divider />

      <Form.Item
        style={{
          textAlign: "center",
          marginTop: "30px",
        }}
      >
        <Button
          key="back"
          htmlType="button"
          onClick={() => {
            resetFields();
            const path = url.split("/").filter(Boolean).splice(0, 3).join("/");
            history.push(`/${path}`);
          }}
        >
          {cancelText}
        </Button>

        <Button
          style={{ marginLeft: "5px" }}
          key="submit"
          htmlType="submit"
          type="primary"
          loading={checkIsLoading(addBillLoading)}
        >
          Создать счет
        </Button>
      </Form.Item>
    </Card>
  );
};
